import { motion } from 'framer-motion';
import { LegacyRef, useEffect, useState } from 'react';
import { FaChevronDown } from 'react-icons/fa6';
import useUserClickObserver from '../../hooks/useUserClickObserver';

interface ISessionDeviceDropdown {
  devices: Array<MediaDeviceInfo>;
  onChangeDevice: (device: MediaDeviceInfo) => void;
  icon: React.ReactNode;
  isDisabled: boolean;
  disabledMsg: string;
}

const SessionDeviceDropdown = ({
  devices,
  onChangeDevice,
  icon,
  isDisabled,
  disabledMsg,
}: ISessionDeviceDropdown) => {
  // integration of react hooks here
  const [showDevices, setShowDevices] = useState<boolean>(false);
  const [selectedDevice, setSelectedDevice] = useState<MediaDeviceInfo | null>(
    null
  );

  // integration of custom hooks here
  const selectRef = useUserClickObserver(setShowDevices);

  useEffect(() => {
    if (devices.length) {
      setSelectedDevice(devices[0]);
    }
  }, [devices]);

  // rendering session device dropdown select component here
  return (
    <div className='relative'>
      <button
        title={selectedDevice?.label || disabledMsg}
        ref={selectRef as LegacyRef<HTMLButtonElement>}
        onClick={() => setShowDevices((state) => !state)}
        className={`relative flex items-center gap-2 w-[250px] bg-base-light-gray p-2 rounded-lg hover:bg-gray-200 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100 duration-300 ${
          isDisabled ? 'text-base-red' : 'text-legal-dark-blue'
        }`}
        disabled={isDisabled}
      >
        {icon}

        <p className='w-[75%] text-left truncate text-sm'>
          {selectedDevice?.label || disabledMsg}
        </p>

        <div
          className={`absolute right-3 top-1/2 -translate-y-1/2 duration-300 bg-transparent ${
            showDevices ? 'rotate-180' : 'rotate-0'
          } ${isDisabled ? 'text-base-red' : 'text-legal-dark-blue'}`}
        >
          <FaChevronDown className='h-6 w-6' />
        </div>
      </button>

      <motion.div
        initial={{ height: 0 }}
        animate={showDevices ? { height: 'fit-content' } : { height: 0 }}
        className='absolute left-0 top-full bg-base-white shadow-academia-sm w-fit z-20 mt-2 rounded-md text-legal-dark-blue overflow-hidden text-base'
      >
        <motion.ul
          initial={{ height: 0 }}
          animate={
            showDevices
              ? devices.length > 5
                ? { height: 220 }
                : { height: 'fit-content' }
              : { height: 0 }
          }
          className='academia-scrollbar overflow-y-auto [&>li:not(:last-child)]:border-b [&>li:not(:last-child)]:border-gray-300'
        >
          {devices.map((device) => (
            <motion.li
              initial={{ opacity: 0 }}
              animate={showDevices ? { opacity: 1 } : { opacity: 0 }}
              key={device.deviceId}
              onClick={() => {
                setSelectedDevice(device);
                onChangeDevice(device);
              }}
              className='py-2 px-3 hover:bg-gray-200 duration-300 cursor-pointer text-nowrap text-sm'
            >
              <p>{device.label}</p>
            </motion.li>
          ))}
        </motion.ul>
      </motion.div>
    </div>
  );
};

export default SessionDeviceDropdown;
